import React, { useEffect, useState } from 'react';
import { Box, Text, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Divider } from '@chakra-ui/react';
import { useBoardContext } from '../context/BoardContext';
import { boardMembers } from '../components/BoardSidebar';
const TEAL = '#00897B';
const BORDER_COLOR = '#006666';
const positions = ['President', 'Vice President', 'Pos 1', 'Pos 2', 'Pos 3', 'Pos 4', 'Pos 5'];
const terms = ['1st', '2nd', '3rd'];
function parseName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  if (!fullName || fullName === 'Vacant') return {
    firstName: '',
    lastName: ''
  };
  const parts = fullName.trim().split(' ');
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return {
    firstName,
    lastName
  };
}
const inputStyles = {
  borderColor: 'gray.300',
  focusBorderColor: TEAL,
  _hover: {
    borderColor: TEAL
  },
  size: 'sm' as const,
  bg: 'white'
};
const labelStyles = {
  fontSize: 'xs' as const,
  fontWeight: '600' as const,
  color: TEAL,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  mb: 0.5
};
export function BoardPage() {
  const {
    selectedIndex
  } = useBoardContext();
  const selected = boardMembers[selectedIndex];
  const {
    firstName: initFirst,
    lastName: initLast
  } = parseName(selected.name);
  const [firstName, setFirstName] = useState(initFirst);
  const [lastName, setLastName] = useState(initLast);
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [position, setPosition] = useState(positions[0]);
  const [term, setTerm] = useState(terms[0]);
  const [startDate, setStartDate] = useState('');
  const [termEndDate, setTermEndDate] = useState('');
  // Re-populate name fields when selection changes
  useEffect(() => {
    const {
      firstName: f,
      lastName: l
    } = parseName(boardMembers[selectedIndex].name);
    setFirstName(f);
    setLastName(l);
    setCompany('');
    setPhone('');
    setEmail('');
    setStreet('');
    setCity('');
    setState('');
    setZip('');
    setPosition(positions[0]);
    setTerm(terms[0]);
    setStartDate('');
    setTermEndDate('');
  }, [selectedIndex]);
  const handleSave = () => {
    // Save logic placeholder
    console.log('Saving board member...');
  };
  return <Box px={6} py={3} maxW="700px" data-id="element-187">
      {/* Heading */}
      <Text fontSize="xl" fontWeight="bold" color="black" mb={3} data-id="element-188">
        Edit Board Member
      </Text>

      <Box border={`2px solid ${BORDER_COLOR}`} borderRadius="md" bg="white" p={4} data-id="element-189">
        {/* Name row */}
        <SimpleGrid columns={2} spacing={3} mb={3} data-id="element-190">
          <FormControl data-id="element-191">
            <FormLabel {...labelStyles} data-id="element-192">First Name</FormLabel>
            <Input {...inputStyles} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First Name" data-id="element-193" />
          </FormControl>
          <FormControl data-id="element-194">
            <FormLabel {...labelStyles} data-id="element-195">Last Name</FormLabel>
            <Input {...inputStyles} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last Name" data-id="element-196" />
          </FormControl>
        </SimpleGrid>

        <FormControl mb={3} data-id="element-197">
          <FormLabel {...labelStyles} data-id="element-198">Company Name</FormLabel>
          <Input {...inputStyles} value={company} onChange={e => setCompany(e.target.value)} placeholder="Company Name" data-id="element-199" />
        </FormControl>

        {/* Address block */}
        <FormControl mb={3} data-id="element-200">
          <FormLabel {...labelStyles} data-id="element-201">Street Address</FormLabel>
          <Input {...inputStyles} value={street} onChange={e => setStreet(e.target.value)} placeholder="Street Address" data-id="element-202" />
        </FormControl>

        <SimpleGrid columns={3} spacing={3} mb={3} data-id="element-203">
          <FormControl data-id="element-204">
            <FormLabel {...labelStyles} data-id="element-205">City</FormLabel>
            <Input {...inputStyles} value={city} onChange={e => setCity(e.target.value)} placeholder="City" data-id="element-206" />
          </FormControl>
          <FormControl data-id="element-207">
            <FormLabel {...labelStyles} data-id="element-208">State</FormLabel>
            <Input {...inputStyles} value={state} onChange={e => setState(e.target.value)} placeholder="State" maxLength={2} data-id="element-209" />
          </FormControl>
          <FormControl data-id="element-210">
            <FormLabel {...labelStyles} data-id="element-211">Zip</FormLabel>
            <Input {...inputStyles} value={zip} onChange={e => setZip(e.target.value)} placeholder="Zip" data-id="element-212" />
          </FormControl>
        </SimpleGrid>

        {/* Phone + Email below address */}
        <SimpleGrid columns={2} spacing={3} mb={3} data-id="element-213">
          <FormControl data-id="element-214">
            <FormLabel {...labelStyles} data-id="element-215">Phone</FormLabel>
            <Input {...inputStyles} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Phone" type="tel" data-id="element-216" />
          </FormControl>
          <FormControl data-id="element-217">
            <FormLabel {...labelStyles} data-id="element-218">Email</FormLabel>
            <Input {...inputStyles} value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" data-id="element-219" />
          </FormControl>
        </SimpleGrid>

        <Divider borderColor="gray.200" my={3} data-id="element-220" />

        {/* Role Info */}
        <SimpleGrid columns={2} spacing={3} mb={3} data-id="element-221">
          <FormControl data-id="element-222">
            <FormLabel {...labelStyles} data-id="element-223">Position</FormLabel>
            <Select value={position} onChange={e => setPosition(e.target.value)} borderColor="gray.300" focusBorderColor={TEAL} _hover={{
            borderColor: TEAL
          }} size="sm" bg="white" data-id="element-224">
              {positions.map(p => <option key={p} value={p} data-id="element-225">
                  {p}
                </option>)}
            </Select>
          </FormControl>
          <FormControl data-id="element-226">
            <FormLabel {...labelStyles} data-id="element-227">Term</FormLabel>
            <Select value={term} onChange={e => setTerm(e.target.value)} borderColor="gray.300" focusBorderColor={TEAL} _hover={{
            borderColor: TEAL
          }} size="sm" bg="white" data-id="element-228">
              {terms.map(t => <option key={t} value={t} data-id="element-229">
                  {t}
                </option>)}
            </Select>
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={2} spacing={3} mb={4} data-id="element-230">
          <FormControl data-id="element-231">
            <FormLabel {...labelStyles} data-id="element-232">Start Date</FormLabel>
            <Input {...inputStyles} value={startDate} onChange={e => setStartDate(e.target.value)} type="date" data-id="element-233" />
          </FormControl>
          <FormControl data-id="element-234">
            <FormLabel {...labelStyles} data-id="element-235">Term End Date</FormLabel>
            <Input {...inputStyles} value={termEndDate} onChange={e => setTermEndDate(e.target.value)} type="date" data-id="element-236" />
          </FormControl>
        </SimpleGrid>

        {/* Save Button */}
        <Button bg="#0d6dfc" color="white" px={8} size="sm" borderRadius="md" fontWeight="semibold" _hover={{
        bg: '#0b5fd9'
      }} _active={{
        bg: '#0a52be'
      }} onClick={handleSave} data-id="element-237">
          Save
        </Button>
      </Box>
    </Box>;
}